import React, { useMemo, useState, useEffect } from "react";
import { Filter, Search, ChevronsLeft, ChevronsRight, X } from "lucide-react";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { useDashboard } from "../context/DashboardContext";
import StatusChip from "./StatusChip";

export default function ProjectStatus() {
  const { dashboardData } = useDashboard();

  const allProjects = useMemo(
    () => dashboardData?.projects_status || [],
    [dashboardData]
  );

  // ✅ default = In Progress
  const [statusFilter, setStatusFilter] = useState("In Progress");
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const itemsPerPage = 5;

  // optional: ensure default when data loads
  useEffect(() => {
    setStatusFilter("In Progress");
  }, [allProjects.length]);

  // reset to first page when filters change
  useEffect(() => {
    setPage(1);
  }, [statusFilter, searchQuery]);

  const statusList = [
    "All",
    "Pending",
    "Started",
    "Planning",
    "Designing",
    "In Progress",
    "Reviewing",
    "Finalizing",
    "On Hold",
    "Completed",
    "Cancelled",
  ];

  const projects = useMemo(() => {
    let list = allProjects;
    if (statusFilter !== "All") {
      list = list.filter((p) => p.status === statusFilter);
    }
    const query = searchQuery.trim().toLowerCase();
    if (query) {
      list = list.filter((p) => p.project_name?.toLowerCase().includes(query));
    }
    return list;
  }, [allProjects, statusFilter, searchQuery]);

  const getProgressColor = (status_color) => {
    switch (status_color) {
      case "green":
        return "bg-gradient-to-r from-green-400 to-green-600";
      case "blue":
        return "bg-gradient-to-r from-blue-400 to-blue-600";
      case "yellow":
        return "bg-gradient-to-r from-yellow-400 to-yellow-500";
      case "red":
        return "bg-gradient-to-r from-red-400 to-red-600";
      case "orange":
        return "bg-gradient-to-r from-orange-400 to-orange-500";
      default:
        return "bg-gray-300";
    }
  };

  const averageProgress = useMemo(() => {
    if (projects.length === 0) return 0;
    return Math.round(
      projects.reduce((sum, p) => sum + Number(p.progress_percentage), 0) /
        projects.length
    );
  }, [projects]);

  const totalPages = Math.max(1, Math.ceil(projects.length / itemsPerPage));
  const paginatedProjects = projects.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  return (
    <section className="h-full p-6 sm:p-8 bg-white rounded-2xl border border-gray-100 shadow-md">

      {/* HEADER */}
      <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-800">
            Project Status
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {projects.length} {projects.length === 1 ? "project" : "projects"} found
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* SEARCH */}
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search project..."
              className="border border-gray-200 bg-gray-50 rounded-lg pl-8 pr-3 py-2 text-sm text-gray-700 w-44 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* DROPDOWN FILTER */}
          <div className="relative">
            <Filter size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-200 bg-gray-50 rounded-lg pl-8 pr-3 py-2 text-sm font-medium text-gray-700 w-44 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none cursor-pointer"
            >
              {statusList.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </div>

          {(searchQuery || statusFilter !== "In Progress") && (
            <button
              type="button"
              onClick={() => {
                setSearchQuery("");
                setStatusFilter("In Progress");
              }}
              className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-red-600 border border-red-200 bg-red-50 hover:bg-red-100 transition whitespace-nowrap shrink-0 w-full sm:w-auto"
            >
              <X size={14} />
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* TABLE */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-100">
              <th className="text-left py-3 px-2 text-xs font-semibold text-gray-500 uppercase tracking-wide w-12">
                Id
              </th>
              <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Project
              </th>
              <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Status
              </th>
              <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide w-32">
                Progress
              </th>
            </tr>
          </thead>

          <tbody>
            {paginatedProjects.map((project, index) => (
              <tr
                key={project.id}
                className="border-b border-gray-50 hover:bg-gray-50 transition-colors duration-150"
              >
                <td className="py-4 px-2 text-sm text-gray-400">
                  {(page - 1) * itemsPerPage + index + 1}
                </td>

                <td className="py-4 px-4 text-sm text-gray-800">
                  {project.project_name}
                </td>

                <td className="py-4 px-4">
                  <StatusChip status={project.status} />
                </td>

                <td className="py-4 px-4">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
                      <div
                        className={`h-2 rounded-full ${getProgressColor(
                          project.status_color
                        )}`}
                        style={{ width: `${project.progress_percentage}%` }}
                      />
                    </div>
                    <span className="text-xs text-gray-600 w-9 text-right">
                      {project.progress_percentage}%
                    </span>
                  </div>
                </td>
              </tr>
            ))}

            {paginatedProjects.length === 0 && (
              <tr>
                <td colSpan={4} className="text-center py-10 text-gray-400 text-sm">
                  No projects found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* FOOTER */}
      <div className="mt-6 pt-4 border-t border-gray-100 flex flex-wrap justify-between items-center gap-3">
        <span className="text-sm text-gray-500">Average Progress</span>
        <span className="text-sm font-bold text-gray-800 bg-gray-50 px-3 py-1 rounded-full">
          {averageProgress}%
        </span>
      </div>

      {/* PAGINATION */}
     {/* PAGINATION */}
{projects.length > itemsPerPage && (
  <div className="flex flex-col sm:flex-row justify-between items-center gap-3 mt-4 pt-4 border-t border-gray-100 text-sm">
    <span className="text-sm text-gray-500 order-2 sm:order-1">
      Showing {(page - 1) * itemsPerPage + 1}-{Math.min(page * itemsPerPage, projects.length)} of {projects.length}
    </span>
    <div className="flex items-center flex-wrap justify-center gap-1 order-1 sm:order-2 w-full sm:w-auto">
      <button
        onClick={() => setPage(1)}
        disabled={page === 1}
        className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        <ChevronsLeft size={16} />
      </button>
      <button
        onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
        disabled={page === 1}
        className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        <MdKeyboardArrowLeft size={16} />
      </button>

      {(() => {
        let startPage = Math.max(page - 1, 1);
        let endPage = startPage + 2;
        if (endPage > totalPages) {
          endPage = totalPages;
          startPage = Math.max(endPage - 2, 1);
        }
        const pages = [];
        for (let i = startPage; i <= endPage; i++) {
          pages.push(
            <button
              key={i}
              onClick={() => setPage(i)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                i === page
                  ? "text-white bg-[linear-gradient(135deg,#2563eb,#d12465)]"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {i}
            </button>
          );
        }
        return pages;
      })()}

      <button
        onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
        disabled={page === totalPages}
        className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        <MdKeyboardArrowRight size={16} />
      </button>
      <button
        onClick={() => setPage(totalPages)}
        disabled={page === totalPages}
        className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        <ChevronsRight size={16} />
      </button>
    </div>
  </div>
)}
    </section>
  );
}
